/**
 * Storage behind the idle sign-out of "remember me" unticked logins: the
 * activity clock every tab shares, the note the login page reads, and the
 * startup rule. Plain functions (no React), so the auth store and the login
 * page can use them; the hook is useIdleSignOut.
 */

import { AUTH_CONFIG, STORAGE_KEYS } from "@/constants";
import { TokenStorage } from "@/api/client";
import { authApi } from "@/api/auth";
import { broadcastAuthEvent } from "@/hooks/useAuthSync";

/** How long the login page keeps explaining an idle sign-out. */
const SIGNOUT_REASON_TTL_MS = 2 * 60 * 60 * 1000;

// This tab's own clock, kept only while localStorage refuses writes (full
// or blocked). Without it a missing stamp would read as a login from before
// the update and sign the user out a second after every login.
let unsharedLastActivity: number | null = null;

export function readLastActivity(): number | null {
  let stored: number | null = null;
  try {
    const value = Number(localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY));
    stored = Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    // Storage blocked: only this tab's clock.
  }
  if (unsharedLastActivity === null) return stored;
  return stored === null ? unsharedLastActivity : Math.max(stored, unsharedLastActivity);
}

export function recordActivity(now: number = Date.now()): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, String(now));
    unsharedLastActivity = null;
  } catch {
    // Storage full or blocked: the tabs can't share a clock; keep this one's.
    unsharedLastActivity = now;
  }
}

/** Remember that the last sign-out was for inactivity (read by the login page). */
export function markSignedOutIdle(now: number = Date.now()): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SIGNOUT_REASON, `idle:${now}`);
  } catch {
    // Storage blocked: the login page just won't explain.
  }
}

/** True when an idle sign-out happened recently (all tabs see it). */
export function wasSignedOutIdle(now: number = Date.now()): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.SIGNOUT_REASON) ?? "";
    const at = Number(value.startsWith("idle:") ? value.slice(5) : NaN);
    return Number.isFinite(at) && now - at < SIGNOUT_REASON_TTL_MS;
  } catch {
    return false;
  }
}

export function clearSignOutReason(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.SIGNOUT_REASON);
  } catch {
    // ignore
  }
}

/**
 * "idle": nobody used the app for the idle limit.
 * "ended": a login from before this sign-out existed, found after the browser
 *   was closed (see check()).
 */
export type IdleSignOutReason = "idle" | "ended";

/** True when this tab was open before the update (the old per-tab marker). */
export function hasLegacyTabMarker(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.SESSION_ACTIVE) === "1";
  } catch {
    return false;
  }
}

/**
 * Where a login stands, for the startup rule and the running idle check:
 * - "ticked": "remember me" ticked, never signed out for inactivity.
 * - "legacy-open": a login from before the idle sign-out (no activity
 *   stamp: every login of this build writes one) in a tab that stayed open
 *   since; it carries on, its clock starting now.
 * - "ended": such a login whose browser was closed, which used to end it.
 *   Its old persistent cookie would otherwise bring it straight back.
 * - "active": unticked, idle for idleMs.
 */
export type IdleState =
  | { kind: "ticked" }
  | { kind: "legacy-open" }
  | { kind: "ended" }
  | { kind: "active"; idleMs: number };

export function idleState(now: number = Date.now()): IdleState {
  if (TokenStorage.isRememberMe()) return { kind: "ticked" };
  const last = readLastActivity();
  if (last === null) return hasLegacyTabMarker() ? { kind: "legacy-open" } : { kind: "ended" };
  return { kind: "active", idleMs: now - last };
}

/**
 * Whether a login found at startup must end without even refreshing it:
 * "idle" when it sat unused past the limit (a browser that restored its tabs
 * and session cookies), "ended" (see IdleState). null: carry on. Deciding
 * before the boot refresh means no refresh races the sign-out.
 */
export function signOutDueAtStartup(now: number = Date.now()): IdleSignOutReason | null {
  const state = idleState(now);
  if (state.kind === "ended") return "ended";
  if (state.kind === "active" && state.idleMs >= AUTH_CONFIG.IDLE_TIMEOUT_MS) return "idle";
  return null;
}

/**
 * Revoke this login server-side, quietly (after a laptop wakes the network
 * may not be back yet), and sign the other tabs out. The caller then clears
 * its own state. Reads the access token synchronously, so call it before
 * clearing it. With `note`, the login page explains the sign-out: only when
 * a tab was open as the time ran out, so someone who just closed the
 * browser (or the next person) isn't told about an idle sign-out.
 */
export function endLoginEverywhere({ note }: { note: boolean }): void {
  if (note) markSignedOutIdle();
  authApi.logout({ skipToast: true }).catch(() => undefined);
  broadcastAuthEvent("logout");
}
